import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Building2, CalendarPlus, Download, Loader2, Pencil, Plus, RefreshCw, Search, Trash2, Upload, UserPlus } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import CommercialeInvitoDialog from "./CommercialeInvitoDialog";
import { AdminPageHeader } from "./components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = `${API}/api/admin/commerciale`;
const PIPELINE_STATES = ["Nuovo", "Contattato", "Demo programmata", "Demo effettuata", "Proposta", "Cliente", "Perso"];
const FONTE_LABELS = {
  registrazione_trial: "Registrazione trial",
  registrazione: "Registrazione",
  invito_onboarding: "Invito onboarding",
  directory_strutture: "Directory",
  import_csv: "Import CSV",
  richiesta_demo: "Richiesta demo",
  fiera_evento: "Fiera / evento",
  lista_terza: "Lista terza",
  manuale: "Manuale",
};

function fonteLabel(fonte) {
  if (!fonte) return "";
  return FONTE_LABELS[fonte] || fonte;
}
const EMPTY_LEAD = {
  nome_struttura: "", referente: "", email: "", telefono: "", tipologia: "", numero_ospiti: "",
  comune: "", provincia: "", stato_pipeline: "Nuovo", commerciale_assegnato: "", fonte: "manuale",
  base_giuridica: "interesse_legittimo", note: "", prossimo_followup: "",
};

function statusClass(status) {
  if (status === "Cliente") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (status === "Perso") return "bg-rose-100 text-rose-800 border-rose-200";
  if (status.includes("Demo")) return "bg-violet-100 text-violet-800 border-violet-200";
  if (status === "Proposta") return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-blue-100 text-blue-800 border-blue-200";
}

function errorMessage(json, fallback) {
  return typeof json?.detail === "string" ? json.detail : fallback;
}

function parseCsvRow(line, delimiter) {
  const out = [];
  let value = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"' && line[index + 1] === '"') {
      value += '"';
      index += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      out.push(value.trim());
      value = "";
    } else {
      value += char;
    }
  }
  out.push(value.trim());
  return out;
}

export default function LeadStruttureAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [items, setItems] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(EMPTY_LEAD);
  const [editId, setEditId] = useState(null);
  const [leadOpen, setLeadOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityLead, setActivityLead] = useState(null);
  const [activity, setActivity] = useState({ tipo: "Chiamata", testo: "", data_prevista: "" });
  const [saving, setSaving] = useState(false);
  const [inviteLead, setInviteLead] = useState(null);
  const csvInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const [res, operatoriRes] = await Promise.all([
        fetch(`${BASE_URL}/prospects?${params.toString()}`, { headers }),
        fetch(`${BASE_URL}/operatori`, { headers }),
      ]);
      const [json, operatoriJson] = await Promise.all([res.json().catch(() => ({})), operatoriRes.json().catch(() => ({}))]);
      if (!res.ok) throw new Error(errorMessage(json, "Impossibile caricare i lead"));
      if (!operatoriRes.ok) throw new Error(errorMessage(operatoriJson, "Impossibile caricare i commerciali"));
      setItems(json.data || []);
      setOperatori(operatoriJson.data || []);
    } catch (error) {
      toast.error(error.message || "Errore nel caricamento dei lead");
    } finally {
      setLoading(false);
    }
  }, [q, status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_LEAD);
    setLeadOpen(true);
  };
  const [leadSummary, setLeadSummary] = useState(null);

  const openEdit = async (lead) => {
    setEditId(lead.id);
    setForm({ ...EMPTY_LEAD, ...lead, numero_ospiti: lead.numero_ospiti ?? "", prossimo_followup: (lead.prossimo_followup || "").slice(0, 10) });
    setLeadSummary({
      responsabile: lead.commerciale_assegnato || "",
      fase: lead.stato_pipeline || "Nuovo",
      followup: lead.prossimo_followup || "",
      prossimaAttivita: null,
    });
    setLeadOpen(true);
    // Dettaglio già esistente: arricchisce solo la striscia di sintesi (prossima attività).
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await fetch(`${BASE_URL}/prospects/${lead.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) return;
      const detail = await response.json();
      const next = (detail.attivita || [])
        .filter((a) => (a.stato || "aperta") === "aperta" && a.data_prevista)
        .sort((a, b) => String(a.data_prevista).localeCompare(String(b.data_prevista)))[0];
      setLeadSummary((current) => ({
        ...(current || {}),
        responsabile: detail.commerciale_assegnato || current?.responsabile || "",
        fase: detail.stato_pipeline || current?.fase || "Nuovo",
        followup: detail.prossimo_followup || current?.followup || "",
        prossimaAttivita: next
          ? `${next.tipo || "Attività"} · ${new Date(next.data_prevista).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}${next.testo ? ` — ${next.testo}` : ""}`
          : null,
      }));
    } catch {
      /* la scheda resta comunque utilizzabile con i dati della lista */
    }
  };

  const saveLead = async () => {
    if (!form.nome_struttura.trim() || !form.referente.trim() || !form.base_giuridica.trim()) {
      toast.error("Inserisci struttura, referente e base giuridica.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, numero_ospiti: form.numero_ospiti === "" ? null : Number(form.numero_ospiti) };
      const res = await fetch(editId ? `${BASE_URL}/prospects/${editId}` : `${BASE_URL}/prospects`, {
        method: editId ? "PUT" : "POST", headers, body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Salvataggio del lead non riuscito"));
      toast.success(editId ? "Lead aggiornato" : "Lead creato");
      setLeadOpen(false);
      load();
    } catch (error) {
      toast.error(error.message || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (lead, stato_pipeline) => {
    try {
      const res = await fetch(`${BASE_URL}/prospects/${lead.id}/stato`, {
        method: "PATCH", headers, body: JSON.stringify({ stato_pipeline }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Aggiornamento stato non riuscito"));
      setItems((current) => current.map((item) => item.id === lead.id ? { ...item, stato_pipeline } : item));
      toast.success("Stato pipeline aggiornato");
    } catch (error) {
      toast.error(error.message || "Errore durante l'aggiornamento");
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Eliminare il lead "${lead.nome_struttura}"?`)) return;
    try {
      const res = await fetch(`${BASE_URL}/prospects/${lead.id}`, { method: "DELETE", headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Eliminazione non riuscita"));
      setItems((current) => current.filter((item) => item.id !== lead.id));
      toast.success("Lead eliminato");
    } catch (error) {
      toast.error(error.message || "Errore durante l'eliminazione");
    }
  };

  const importDirectory = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/prospects/import-directory`, { method: "POST", headers, body: JSON.stringify({}) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Importazione directory non riuscita"));
      toast.success(`Directory importata: ${json.created || 0} creati, ${json.skipped || 0} già presenti`);
      load();
    } catch (error) {
      toast.error(error.message || "Errore durante l'importazione");
    } finally {
      setSaving(false);
    }
  };

  const importCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setSaving(true);
    try {
      const content = await file.text();
      const lines = content.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) throw new Error("Il CSV deve contenere intestazione e almeno una riga.");
      const delimiter = lines[0].includes(";") ? ";" : ",";
      const headers = parseCsvRow(lines[0], delimiter).map((item) => item.toLowerCase().trim().replace(/\s+/g, "_"));
      const rows = lines.slice(1).map((line) => {
        const values = parseCsvRow(line, delimiter);
        const raw = Object.fromEntries(headers.map((key, index) => [key, values[index] || ""]));
        return {
          nome_struttura: raw.nome_struttura || raw.struttura || raw.nome || "",
          referente: raw.referente || raw.nome_referente || "",
          email: raw.email || raw.email_struttura || "",
          telefono: raw.telefono || raw.telefono_struttura || "",
          tipologia: raw.tipologia || raw.tipo_struttura || "",
          numero_ospiti: raw.numero_ospiti ? Number(raw.numero_ospiti) : null,
          comune: raw.comune || "",
          provincia: raw.provincia || "",
          stato_pipeline: raw.stato_pipeline || "Nuovo",
          commerciale_assegnato: raw.commerciale_assegnato || "",
          fonte: raw.fonte || "import_csv",
          base_giuridica: raw.base_giuridica || "interesse_legittimo",
          note: raw.note || "",
          prossimo_followup: raw.prossimo_followup || null,
        };
      });
      const response = await fetch(`${BASE_URL}/prospects/import`, {
        method: "POST", headers, body: JSON.stringify({ rows }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(errorMessage(json, "Importazione CSV non riuscita"));
      toast.success(`CSV importato: ${json.created || 0} creati, ${json.skipped || 0} scartati`);
      load();
    } catch (error) {
      toast.error(error.message || "Errore durante l'importazione CSV");
    } finally {
      setSaving(false);
    }
  };

  const saveActivity = async () => {
    if (!activity?.testo.trim()) {
      toast.error("Descrivi l'attività da pianificare.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/prospects/${activityLead.id}/attivita`, {
        method: "POST", headers, body: JSON.stringify(activity),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Creazione attività non riuscita"));
      toast.success("Attività pianificata");
      setActivityOpen(false);
    } catch (error) {
      toast.error(error.message || "Errore durante la creazione dell'attività");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={Building2}
        title="Lead strutture"
        description="Prospect commerciali e consenso di contatto. Le registrazioni autonome (trial o piano a pagamento) compaiono qui automaticamente. Da qui puoi anche invitare il referente a completare la registrazione Affidya."
        actions={
          <>
            <input ref={csvInput} type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} />
            <Button variant="outline" size="sm" onClick={importDirectory} disabled={saving}><Download className="mr-1 h-4 w-4" /> Importa directory</Button>
            <Button variant="outline" size="sm" onClick={() => csvInput.current?.click()} disabled={saving}><Upload className="mr-1 h-4 w-4" /> Importa CSV</Button>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna</Button>
            <Button size="sm" onClick={openCreate}><Plus className="mr-1 h-4 w-4" /> Nuovo lead</Button>
          </>
        }
      />

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[220px] flex-1">
          <Label className="text-xs">Cerca</Label>
          <div className="relative"><Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" value={q} onChange={(event) => setQ(event.target.value)} placeholder="Struttura, referente, comune…" /></div>
        </div>
        <div className="w-48">
          <Label className="text-xs">Stato pipeline</Label>
          <Select value={status || "__all__"} onValueChange={(value) => setStatus(value === "__all__" ? "" : value)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">Tutti gli stati</SelectItem>{PIPELINE_STATES.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="min-w-[11rem] p-3">Struttura</th>
                <th className="p-3">Referente</th>
                <th className="hidden p-3 2xl:table-cell">Località</th>
                <th className="p-3">Responsabile</th>
                <th className="w-[10rem] p-3">Fase</th>
                <th className="hidden whitespace-nowrap p-3 lg:table-cell">Follow-up</th>
                <th className="sticky right-0 z-10 whitespace-nowrap bg-slate-50 p-3 shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.15)]">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? <tr><td colSpan="7" className="p-8 text-center text-slate-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Caricamento…</td></tr>
                : !items.length ? <tr><td colSpan="7" className="p-8 text-center text-slate-500">Nessun lead trovato con i filtri attuali.</td></tr>
                : items.map((lead) => (
                <tr
                  key={lead.id}
                  className="group cursor-pointer hover:bg-admin-accent-soft/40"
                  onClick={() => openEdit(lead)}
                  onKeyDown={(event) => { if (event.target !== event.currentTarget) return; if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openEdit(lead); } }}
                  tabIndex={0}
                  aria-label={`Apri scheda ${lead.nome_struttura}`}
                >
                  <td className="p-3 align-top">
                    <p className="font-medium leading-snug break-words">{lead.nome_struttura}</p>
                    <p className="text-xs text-slate-500">{lead.tipologia || "Tipologia non indicata"} · {lead.numero_ospiti ?? "—"} ospiti</p>
                    <p className="text-xs text-slate-500 2xl:hidden">{[lead.comune, lead.provincia].filter(Boolean).join(" ") || "Località non indicata"}</p>
                    {fonteLabel(lead.fonte) ? <p className="mt-0.5 text-[11px] text-indigo-600">{fonteLabel(lead.fonte)}</p> : null}
                  </td>
                  <td className="max-w-[13rem] p-3 align-top"><p className="break-words">{lead.referente || "—"}</p><p className="break-all text-xs text-slate-500">{lead.email || lead.telefono || "—"}</p></td>
                  <td className="hidden p-3 align-top 2xl:table-cell">{[lead.comune, lead.provincia].filter(Boolean).join(" ") || "—"}</td>
                  <td className="max-w-[12rem] p-3 align-top text-sm"><span className="break-all" title={lead.commerciale_assegnato || ""}>{lead.commerciale_assegnato || <span className="text-slate-400">Non assegnato</span>}</span></td>
                  <td className="p-3 align-top" onClick={(event) => event.stopPropagation()}>
                    <Select value={lead.stato_pipeline || "Nuovo"} onValueChange={(value) => updateStatus(lead, value)}>
                      <SelectTrigger className="h-8 w-full max-w-[9.5rem]"><Badge className={statusClass(lead.stato_pipeline || "Nuovo")}>{lead.stato_pipeline || "Nuovo"}</Badge></SelectTrigger>
                      <SelectContent>{PIPELINE_STATES.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="hidden whitespace-nowrap p-3 align-top lg:table-cell">{lead.prossimo_followup ? new Date(lead.prossimo_followup).toLocaleDateString("it-IT") : "—"}</td>
                  <td className="sticky right-0 z-10 bg-admin-surface p-2 group-hover:bg-admin-accent-soft/40 align-top shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.12)]" onClick={(event) => event.stopPropagation()}>
                    <div className="flex flex-nowrap items-center gap-0.5">
                      <Button size="icon" variant="ghost" title="Apri scheda lead" aria-label={`Apri scheda ${lead.nome_struttura}`} onClick={() => openEdit(lead)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" title="Invita su Affidya" aria-label={`Invita ${lead.nome_struttura} su Affidya`} onClick={() => setInviteLead(lead)}><UserPlus className="h-4 w-4 text-admin-accent" /></Button>
                      <Button size="icon" variant="ghost" title="Nuova attività" aria-label={`Attività per ${lead.nome_struttura}`} onClick={() => { setActivityLead(lead); setActivity({ tipo: "Chiamata", testo: "", data_prevista: "" }); setActivityOpen(true); }}><CalendarPlus className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" title="Elimina lead" aria-label={`Elimina ${lead.nome_struttura}`} onClick={() => deleteLead(lead)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <Dialog open={leadOpen} onOpenChange={(open) => { setLeadOpen(open); if (!open) setLeadSummary(null); }}><DialogContent className="max-h-[92vh] w-[96vw] max-w-5xl overflow-y-auto"><DialogHeader><DialogTitle>{editId ? "Scheda lead struttura" : "Nuovo lead struttura"}</DialogTitle></DialogHeader>
        {editId ? (
          <div className="grid gap-3 rounded-xl border border-admin-border bg-admin-accent-soft/40 p-3 sm:grid-cols-2 lg:grid-cols-4" aria-label="Riepilogo lead">
            <div><p className="text-[11px] font-semibold uppercase tracking-wide text-admin-muted">Responsabile</p><p className="break-all text-sm font-medium text-admin-text">{form.commerciale_assegnato || leadSummary?.responsabile || "Non assegnato"}</p></div>
            <div><p className="text-[11px] font-semibold uppercase tracking-wide text-admin-muted">Fase</p><Badge className={statusClass(form.stato_pipeline || leadSummary?.fase || "Nuovo")}>{form.stato_pipeline || leadSummary?.fase || "Nuovo"}</Badge></div>
            <div><p className="text-[11px] font-semibold uppercase tracking-wide text-admin-muted">Prossimo follow-up</p><p className="text-sm font-medium text-admin-text">{(form.prossimo_followup || leadSummary?.followup) ? new Date(form.prossimo_followup || leadSummary.followup).toLocaleDateString("it-IT") : "Non pianificato"}</p></div>
            <div className="sm:col-span-2 lg:col-span-1"><p className="text-[11px] font-semibold uppercase tracking-wide text-admin-muted">Prossima attività</p><p className="text-sm font-medium leading-snug text-admin-text">{leadSummary?.prossimaAttivita || "Nessuna attività aperta"}</p></div>
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[["nome_struttura", "Nome struttura *"], ["referente", "Referente *"], ["email", "Email", "email"], ["telefono", "Telefono", "tel"], ["tipologia", "Tipologia"], ["numero_ospiti", "Numero ospiti", "number"], ["comune", "Comune"], ["provincia", "Provincia"], ["fonte", "Fonte"]].map(([key, label, type]) => <div key={key}><Label className="text-xs">{label}</Label><Input type={type || "text"} value={form[key]} onChange={(event) => setField(key, event.target.value)} /></div>)}
          <div><Label className="text-xs">Commerciale assegnato</Label><Select value={form.commerciale_assegnato || "__me__"} onValueChange={(value) => setField("commerciale_assegnato", value === "__me__" ? "" : value)}><SelectTrigger><SelectValue placeholder="Io (alla creazione)" /></SelectTrigger><SelectContent><SelectItem value="__me__">Io (alla creazione)</SelectItem>{operatori.map((operator) => <SelectItem key={operator.id || operator.email} value={operator.email}>{operator.nome || operator.name || operator.email}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Stato pipeline</Label><Select value={form.stato_pipeline} onValueChange={(value) => setField("stato_pipeline", value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PIPELINE_STATES.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Prossimo follow-up</Label><Input type="date" value={form.prossimo_followup} onChange={(event) => setField("prossimo_followup", event.target.value)} /></div>
          <div className="sm:col-span-2 lg:col-span-3"><Label className="text-xs">Base giuridica del contatto *</Label><Input value={form.base_giuridica} onChange={(event) => setField("base_giuridica", event.target.value)} placeholder="Es. legittimo interesse, consenso…" /></div>
          <div className="sm:col-span-2 lg:col-span-3"><Label className="text-xs">Note</Label><Textarea rows={4} value={form.note} onChange={(event) => setField("note", event.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setLeadOpen(false)}>Annulla</Button><Button onClick={saveLead} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva</Button></DialogFooter>
      </DialogContent></Dialog>

      <Dialog open={activityOpen} onOpenChange={setActivityOpen}><DialogContent><DialogHeader><DialogTitle>Nuova attività · {activityLead?.nome_struttura}</DialogTitle></DialogHeader>
        <div className="space-y-3"><div><Label>Tipo</Label><Select value={activity.tipo} onValueChange={(value) => setActivity((current) => ({ ...current, tipo: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Chiamata", "Email", "Appuntamento", "Follow-up", "Altro"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select></div><div><Label>Testo *</Label><Textarea value={activity.testo} onChange={(event) => setActivity((current) => ({ ...current, testo: event.target.value }))} /></div><div><Label>Data prevista</Label><Input type="datetime-local" value={activity.data_prevista} onChange={(event) => setActivity((current) => ({ ...current, data_prevista: event.target.value }))} /></div></div>
        <DialogFooter><Button variant="outline" onClick={() => setActivityOpen(false)}>Annulla</Button><Button onClick={saveActivity} disabled={saving}>Pianifica</Button></DialogFooter>
      </DialogContent></Dialog>

      <CommercialeInvitoDialog
        open={!!inviteLead}
        onOpenChange={(open) => { if (!open) setInviteLead(null); }}
        lead={inviteLead}
        onCreated={load}
      />
    </div>
  );
}
